import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { customFetchV2 } from '../../../services/custom-fetch-v2.ts';
import Switch from '../../bits/Switch.jsx';
import TileV2 from '../../bits/TileV2.tsx';
import TileActionRow from '../../bits/TileActionRow.jsx';
import TileActionRowLink from '../../bits/TileActionRowLink.jsx';
import Loading from '../Loading.jsx';

const Features = Object.freeze({
  TRIPS: 'trips_feature',
  SPEED_ALERTS: 'speed_alerts_feature',
  LOW_BATTERY_ALERTS: 'battery_feature',
  HARSH_DRIVING: 'harsh_driving_feature',
  DEVICE_DISCONNECT_ALERTS: 'device_disconnect_alerts_feature',
  TAMPER_ALERTS: 'tamper_alerts_feature',
});

const AlertRules = Object.freeze({
  TRIPS: 'DeviceAlertRules::TripsAlertRule',
  SPEEDING: 'DeviceAlertRules::SpeedingAlertRule',
  LOW_BATTERY: 'DeviceAlertRules::LowBatteryAlertRule',
  HARSH_DRIVING: 'DeviceAlertRules::HarshDrivingAlertRule',
  DEVICE_DISCONNECT: 'DeviceAlertRules::DisconnectAlertRule',
  TAMPER: 'DeviceAlertRules::TamperAlertRule',
});

class DeviceAlertOptionslPanel extends Component {
  state = {
    isLoading: true,
    alertRules: [],
    errorMessage: null,
  }

  componentDidMount() {
    this.loadAlertRules();
  }

  loadAlertRules = () => {
    const deviceId = this.props.device.id;

    return customFetchV2(`/api/v2/devices/${deviceId}/alert-rules`, {
      method: 'GET',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load device alert rules for profile ${deviceId} - HTTP status ${response.status}`);
        }
        return response.data;
      })
      .then((alertRules) => {
        this.setState({
          isLoading: false,
          alertRules,
        });
      })
      .catch((error) => {
        this.setState({
          errorMessage: error.message,
          isLoading: false,
        });
      });
  }

  handleSwitchChange = (event) => {
    const deviceId = this.props.device.id;
    const ruleTag = event.target.name;

    const rule = this.setAlertRuleEnabled(ruleTag, event.target.checked);

    customFetchV2(`/api/v2/devices/${deviceId}/alert-rules/${ruleTag}`, {
      method: 'PUT',
      body: JSON.stringify(rule),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to update device alert rules for profile ${deviceId} - HTTP status ${response.status}`);
        }
        return response.data;
      })
      .then(() => {
        this.setState({
          isLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          errorMessage: error.message,
          isLoading: false,
        });
      });
  }

  setAlertRuleEnabled = (ruleId, enabled) => {
    const alertRules = [...this.state.alertRules];
    let rule = alertRules.find(e => e.ruleId === ruleId);
    if (!rule) {
      rule = { ruleId, type: ruleId };
      alertRules.push(rule);
    }

    rule.enabled = enabled;
    this.setState({ alertRules });
    return rule;
  }

  isRuleEnabled = (ruleId) => {
    const rule = this.getRule(ruleId);
    return rule ? rule.enabled : false;
  }

  getRule = ruleId => this.state.alertRules.find(e => e.ruleId === ruleId);

  getSpeedRuleDescription = () => {
    const rule = this.getRule(AlertRules.SPEEDING);
    if (!rule || !rule.enabled) {
      return 'Off';
    }

    return `Limit ${rule.params.speed} mph`;
  }

  render() {
    const { device, features } = this.props;
    const { isLoading } = this.state;

    if (isLoading) {
      return (
        <div className="section">
          <Loading />
        </div>
      );
    }

    return (
      <TileV2
        title="Alert Settings"
        subtitle="Device specific alerts"
        disabled={this.props.disabled}>
        { features.includes(Features.TRIPS) ? (
          <TileActionRow label="Completed Trips" action={
            <Switch
              name={AlertRules.TRIPS}
              className="up-8"
              edge="end"
              onChange={this.handleSwitchChange}
              checked={this.isRuleEnabled(AlertRules.TRIPS)}
              inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
            />} />
        ) : null}

        { features.includes(Features.HARSH_DRIVING) ? (
          <TileActionRow label="Hazardous Driving" action={
            <Switch
              name={AlertRules.HARSH_DRIVING}
              className="up-8"
              edge="end"
              onChange={this.handleSwitchChange}
              checked={this.isRuleEnabled(AlertRules.HARSH_DRIVING)}
              inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
            />} />
        ) : null}

        { features.includes(Features.LOW_BATTERY_ALERTS) ? (
          <TileActionRow label="Low Battery" action={
            <Switch
              name={AlertRules.LOW_BATTERY}
              className="up-8"
              edge="end"
              onChange={this.handleSwitchChange}
              checked={this.isRuleEnabled(AlertRules.LOW_BATTERY)}
              inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
            />} />
        ) : null}

        { features.includes(Features.TAMPER_ALERTS) ? (
          <TileActionRow label="Tamper Detection" action={
            <Switch
              name={AlertRules.TAMPER}
              className="up-8"
              edge="end"
              onChange={this.handleSwitchChange}
              checked={this.isRuleEnabled(AlertRules.TAMPER)}
              inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
            />} />
        ) : null}

        { features.includes(Features.DEVICE_DISCONNECT_ALERTS) ? (
          <TileActionRow label="Tracker Disconnected" action={
            <Switch
              name={AlertRules.DEVICE_DISCONNECT}
              className="up-8"
              edge="end"
              onChange={this.handleSwitchChange}
              checked={this.isRuleEnabled(AlertRules.DEVICE_DISCONNECT)}
              inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
            />} />
        ) : null}

        { features.includes(Features.SPEED_ALERTS) ? (
          <TileActionRowLink
            label="Speeding"
            description={this.getSpeedRuleDescription()}
            to={{
              pathname: `/m/devices/${device.id}/edit-speed-alert`,
              state: { origin: 'device_alert_options' },
            }}
           />
        ) : null}
      </TileV2>
    );
  }
}

DeviceAlertOptionslPanel.propTypes = {
  device: PropTypes.object.isRequired,
  features: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
};

export default DeviceAlertOptionslPanel;
